import {
  getTicketSaleKey,
  getTicketSalesKey,
  TicketSaleModel,
  useOpenTicketTicketSale,
} from 'api-hooks/sale/ticket-sale';
import { CloseCircle, EditSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { SaleStatusEnum } from 'common/constants/enum';
import { queryClient } from 'common/repositories/query-client';
import { themedStyled } from 'common/styles/styletron.theme';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Button } from 'components/elements/buttons';
import { MasterDatePicker, MasterInput } from 'components/elements/master';
import Text from 'components/elements/text';
import useDialog from 'hooks/use-dialog';
import useNavigation from 'hooks/use-navigation';
import {
  ActionContainer,
  CardSection,
  FullContainer,
  HalfContainer,
  SectionContainer as RawSectionContainer,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { StyleObject } from 'styletron-standard';

import OpenTicketCancelDialog from './cancel-dialog';
import {
  ScheduleCardHeader,
  ScheduleCardHeaderLeft,
  ScheduleCardHeaderRight,
} from '../../style';

interface Props {
  ticketSale: TicketSaleModel;
}

export default function OpenTicketCard(props: Props) {
  const { t } = useTranslation();
  const { ticketSale } = props;
  const { close, navigate } = useNavigation();
  const dialog = useDialog();
  const { mutateAsync } = useOpenTicketTicketSale();

  const onCancel = () => {
    dialog.showCustom({
      render: (dismiss) => (
        <OpenTicketCancelDialog
          onConfirm={async () => {
            try {
              dismiss();
              const result = await mutateAsync({
                flightSaleRefId: ticketSale.refId,
                body: { isOpenTicket: false },
              });
              queryClient.refetchQueries([getTicketSalesKey()[0]]);
              queryClient.refetchQueries(
                getTicketSaleKey({ id: ticketSale.id }),
              );
              notification.success({ message: result.message });
            } catch (error) {
              notification.error({ message: error.message });
            }
          }}
          onCancel={() => dismiss()}
        />
      ),
    });
  };

  const onSetReschedule = () => {
    close();
    navigate(NavigationRoutes.Reschedule, {
      data: ticketSale,
      type: 'flight',
    });
  };

  return (
    <>
      <CardSection>
        <ScheduleCardHeader $style={styles.header}>
          <ScheduleCardHeaderLeft $style={styles.leftHeader}>
            <Text variant="HeadingXSmall">{t('common:open_ticket')}</Text>
          </ScheduleCardHeaderLeft>
          <ScheduleCardHeaderRight $style={styles.rightHeader}>
            <Button
              buttonType="secondary"
              isError
              onClick={onCancel}
              startEnhancer={(color, size) => (
                <CloseCircle width={size} height={size} color={color} />
              )}
            >
              {t('common:cancel')}
            </Button>
            {ticketSale.status === SaleStatusEnum.OpenTicket && (
              <>
                <Separator gap={16} />
                <Button
                  onClick={onSetReschedule}
                  startEnhancer={(color, size) => (
                    <ActionContainer $style={{ marginRight: '8px' }}>
                      <EditSVG size={size} color={color} />
                    </ActionContainer>
                  )}
                >
                  {t('ticketSales:set_reschedule')}
                </Button>
              </>
            )}
          </ScheduleCardHeaderRight>
        </ScheduleCardHeader>
        <Separator gap={16} />
        <SectionContainer>
          <HalfContainer>
            <MasterDatePicker
              disabled
              label={t('ticketSales:last_reissue_date')}
              value={ticketSale.lastReissueDate}
            />
          </HalfContainer>
          <HalfContainer>
            <MasterDatePicker
              disabled
              label={t('ticketSales:last_travelling_date')}
              value={ticketSale.lastTravellingDate}
            />
          </HalfContainer>
        </SectionContainer>
        <FullContainer>
          <MasterInput
            disabled
            value={ticketSale.ticketDescription}
            label={t('common:description')}
            noMargin
          />
        </FullContainer>
      </CardSection>
      <Separator gap={24} />
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});

const styles: { [key: string]: StyleObject | any } = {
  header: {
    flexDirection: 'column',
    '@media screen and (min-width: 576px)': {
      flexDirection: 'row',
    },
  },
  leftHeader: {
    flexBasis: '30%',
  },
  rightHeader: {
    flexBasis: '70%',
    justifyContent: 'end',
    marginTop: '8px',
    '@media screen and (min-width: 576px)': {
      marginTop: '0',
    },
  },
};
